import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from '../../components/Picker/DatePicker';
import TimePicker from '../../components/Picker/TimePicker';
import OrderItem from '../../components/Order/OrderItem';
import OrderPayment from '../../components/Order/OrderPayment';
import ProductStockPop from '../../components/Popup/ProductStockPopup';
import EquipmentRatingPopup from '../../components/Popup/EquipmentRatingPopup';
import dayjs from 'dayjs';
import o_select from "../../assets/icon/order_select.svg";
import o_order from "../../assets/icon/order_.svg";
import o_receive from "../../assets/icon/order_receive.svg";
import o_rental from "../../assets/icon/order_rental.svg";
import o_return from "../../assets/icon/order_return.svg";
import next from "../../assets/icon/16_next_gray.svg";
import product from "../../assets/img/detailproduct.png";
import { STORE_LIST_URL, FOOTER_DATA_URL } from "../../utils/urls";
import instance from "../../services/services";
import orderApi from "../../services/Order/orderApi";
import { debounce } from 'lodash';
import { formatLocalString } from "../../utils/validation";
import PaymentService from "../../services/Payment/PaymentService";
import PopupLoginError from "../../components/Popup/LoginError";
import { useLanguage } from "../../hooks/useLanguage";
import useUserStore from "../../store/userStore";
import { useOrderCount } from './OrderView';
import TermsPopup from "../../components/Popup/TermsPopup";
import { TERMS_LIST_URL } from "../../utils/urls";

// 디바운스된 API 호출 함수 생성 (컴포넌트 외부에)
const debouncedFn = debounce((callback) => {
  callback();
}, 300);

// formatTime 함수 추가 (컴포넌트 외부에)
const formatTime = (hour, min) => {
  const formattedHour = String(hour).padStart(2, '0');
  const formattedMin = String(min).padStart(2, '0');
  return `${formattedHour}:${formattedMin}`;
};

// createRequestParams를 컴포넌트 외부로 이동
const createRequestParams = (rentalState, returnState, rentalDateTime, returnDateTime, selectedProductIds) => {
  // 방문주문의 경우 시간이 선택되지 않았을 경우 금액계산이 안되도록 하기위해 시간이 선택되지 않았을 경우 날짜만 넣어줌
  const rental_sdate = rentalDateTime?.time
                      ? `${rentalDateTime?.date.format('YYYY-MM-DD')} ${rentalDateTime.time.replace('시', ':00')}`
                      : rentalDateTime?.date.format('YYYY-MM-DD');

  const rental_edate = returnDateTime?.time
                      ? `${returnDateTime?.date.format('YYYY-MM-DD')} ${returnDateTime.time.replace('시', ':00')}`
                      : returnDateTime?.date.format('YYYY-MM-DD');

  return {
    order_kind: "visit",
    rental_store_seq: rentalState?.selectValue || "",
    return_store_seq: returnState?.selectValue || "",
    rental_sdate: rental_sdate,
    rental_edate: rental_edate,
    additional_sale: "",
    point: "",
    allPointUse: "N",
    download_seq: "",
    balance: "",
    cart_select_goods: selectedProductIds || [],
    pay_select_goods: [],
    undecidedChk: "N",
    balance_price: "", // 예치금 관련 추가
    allBalanceUse: "N" // 예치금 관련 추가
  };
};

function VisitOrder(){
  const { t } = useLanguage();
  const navigate = useNavigate();
  const rentalSelectRef = useRef(null);
  const returnSelectRef = useRef(null);
  const [memo, setMemo] = useState('');
  const [isPopupLoginError, setIsPopupLoginError] = useState(""); // 팝업 관리
  const [storeList, setStoreList] = useState([]); // 대여 지점 리스트
  const [isShow, setIsShow] = useState({}); // 빈 객체로 초기화
  const { memberTokens, userInfo } = useUserStore();
  const [rentalState, setRentalState] = useState({
    isOpen: false,
    selectItem: '',
    selectValue: '',
    storeInfo: null
  });

  const [returnState, setReturnState] = useState({
    isOpen: false,
    selectItem: '',
    selectValue: '',
    storeInfo: null
  });

  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isChecked, setIsChecked] = useState({
    option1: false,
    option2: false,
    option3: false,
    option4: false,
  });

  // 재고 여부 팝업
  const [isStockPopup, setStockPopup] = useState(false);
  const [stockData, setStockData] = useState([]);

  // 장비평점 팝업
  const [isEquipmentPopup, setEquipmentPopup ] = useState(false);
  const handleEquipmentPopup = () => {
    setEquipmentPopup(true)
  };



  // 대여지점 리스트
  useEffect(() => {
    const fetchStoreList = async () => {
      try {
        const response = await instance.post(STORE_LIST_URL);
        setStoreList(response.data.data)
      } catch (error) {
        console.error('대여지점 리스트 가져오기 실패:', error);
      }
    };
    fetchStoreList();
  }, []);

  // 대여 날짜/시간 상태 추가
  const [rentalDateTime, setRentalDateTime] = useState({
    date: dayjs(),
    time: ''
  });

  // 반납 날짜/시간 상태 추가
  const [returnDateTime, setReturnDateTime] = useState({
    date: dayjs(),
    time: ''
  });

  // 상태 추가
  const [cartListData, setCartListData] = useState({
    bundleMain: [],
    bundleCp: [], // bundleAdd에서 bundleCp로 변경
    bundleAcc: []
  });
  const [orderSummary, setOrderSummary] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedCounts, setSelectedCounts] = useState({
    main: 0,
    add: 0,
    acc: 0
  });

  // 선택된 상품 처리를 위한 ref 추가
  const selectedProductIdsRef = useRef([]);
  const didSetInitialSelection = useRef(false);

  // selectedProductIds 변경 시 ref 업데이트
  useEffect(() => {
    selectedProductIdsRef.current = selectedProductIds;
  }, [selectedProductIds]);

  // 상태 추가
  const [selectedRentalStore, setSelectedRentalStore] = useState('');
  const [selectedReturnStore, setSelectedReturnStore] = useState('');

  // 대여 지점 선택 핸들러 수정
  const handleRentalSelectItem = (store) => {
    setSelectedRentalStore(store.store_name); // 선택한 대여 지점 저장

    // 24시간 영업 여부 확인
    // opening_hour_set이 'N'이거나 시작/종료 시간이 모두 00인 경우
    const is24Hours = store.opening_hour_set === 'N' ||
                      (store.opening_start_hour === '00' && store.opening_end_hour === '00');

    setRentalState({
      selectItem: store.store_name,
      selectValue: store.store_seq,
      isOpen: false,
      storeInfo: {
        startHour: store.opening_start_hour,
        startMin: store.opening_start_min,
        endHour: store.opening_end_hour,
        endMin: store.opening_end_min,
        isFullTime: is24Hours // 24시간 영업 여부로 수정
      }
    });

    // 결제 폼 초기화
    resetPaymentForm();

    // API 호출 수정
    const params = {
      ...createRequestParams(
        { ...rentalState, selectValue: store.store_seq },
        returnState,
        rentalDateTime,
        returnDateTime,
        selectedProductIdsRef.current
      )
    };

    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            // 기존 체크 상태 유지하면서 데이터 업데이트
            setCartListData(prevCart => ({
              bundleMain: data.cartList?.bundleMain?.map(item => ({
                ...item,
                isChecked: true // 대여상품은 체크 상태 유지
              })) || [],
              bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: false // 부가품목은 체크 해제 상태 유지
              })) || [],
              bundleAcc: data.cartList?.bundleAcc?.map(item => ({
                ...item,
                isChecked: false // 액세서리는 체크 해제 상태 유지
              })) || []
            }));
            setOrderSummary(data);

            // selectedProductIds 업데이트 (부가품목 제외)
            const mainIds = data.cartList?.bundleMain?.map(item => item.cart_option_seq) || [];
            // const accIds = data.cartList?.bundleAcc?.map(item => item.cart_option_seq) || [];
            setSelectedProductIds([...mainIds]);
          }
        })
        .catch(error => {
          console.error('대여 지점 선택 중 오류:', error);
        });
    });
  };

  // 반납 지점 선택 핸들러 수정
  const handleReturnSelectItem = (store) => {
    setSelectedReturnStore(store.store_name);

    const is24Hours = store.opening_hour_set === 'N' ||
                      (store.opening_start_hour === '00' && store.opening_end_hour === '00');

    setReturnState({
      selectItem: store.store_name,
      selectValue: store.store_seq,
      isOpen: false,
      storeInfo: {
        startHour: store.opening_start_hour,
        startMin: store.opening_start_min,
        endHour: store.opening_end_hour,
        endMin: store.opening_end_min,
        isFullTime: is24Hours
      }
    });

    // 결제 폼 초기화
    resetPaymentForm();

    const params = {
      ...createRequestParams(
        rentalState,
        { ...returnState, selectValue: store.store_seq },
        rentalDateTime,
        returnDateTime,
        selectedProductIdsRef.current
      )
    };

    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            setCartListData(prevCart => ({
              bundleMain: data.cartList?.bundleMain?.map(item => ({
                ...item,
                isChecked: true // 대여상품은 체크 상태 유지
              })) || [],
              bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: false // 부가품목은 체크 해제 상태 유지
              })) || [],
              bundleAcc: data.cartList?.bundleAcc?.map(item => ({
                ...item,
                isChecked: false // 액세서리는 체크 해제 상태 유지
              })) || []
            }));
            setOrderSummary(data);

            // selectedProductIds 업데이트 (부가품목 제외)
            const mainIds = data.cartList?.bundleMain?.map(item => item.cart_option_seq) || [];
            const accIds = data.cartList?.bundleAcc?.map(item => item.cart_option_seq) || [];
            setSelectedProductIds([...mainIds, ...accIds]);
          }
        })
        .catch(error => {
          console.error('반납 지점 선택 중 오류:', error);
        });
    });
  };

  // 대여 시간 변경 핸들러 수정
  const handleRentalTimeChange = (newTime) => {
    // 대여 시간 업데이트
    setRentalDateTime(prev => ({ ...prev, time: newTime }));

    // 반납 시간 초기화
    setReturnDateTime(prev => ({ ...prev, time: '' }));

    // 결제 폼 초기화
    resetPaymentForm();

    const params = {
      ...createRequestParams(rentalState, returnState,
        { ...rentalDateTime, time: newTime },
        { ...returnDateTime, time: '' }, // 반납 시간을 빈 문자열로 설정
        selectedProductIdsRef.current
      )
    };

    orderApi.getCartDetail(params)
      .then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          setCartListData(prevCart => ({
            bundleMain: data.cartList?.bundleMain?.map(item => ({
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || [],
            bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || [],
            bundleAcc: data.cartList?.bundleAcc?.map(item => ({
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || []
          }));
          setOrderSummary(data); // 주문 요약 정보 업데이트
        }
      })
      .catch(error => {
        console.error('대여 시간 변경 중 오류:', error);
      });
  };

  // 반납 시간 변경 핸들러 수정
  const handleReturnTimeChange = (newTime) => {
    setReturnDateTime(prev => ({ ...prev, time: newTime }));

    // 결제 폼 초기화
    resetPaymentForm();

    const params = {
      ...createRequestParams(rentalState, returnState,
        rentalDateTime,
        { ...returnDateTime, time: newTime },
        selectedProductIdsRef.current
      )
    };

    orderApi.getCartDetail(params)
      .then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          setCartListData(prevCart => ({
            bundleMain: data.cartList?.bundleMain?.map(item => ({
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || [],
            bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || [],
            bundleAcc: data.cartList?.bundleAcc?.map(item => ({
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || []
          }));
          setOrderSummary(data); // 주문 요약 정보 업데이트
        }
      })
      .catch(error => {
        console.error('반납 시간 변경 중 오류:', error);
      });
  };

  // 대여 날짜 변경 핸들러 수정
  const handleRentalDateChange = (newDate) => {
    // 대여 날짜 업데이트 및 시간 초기화
    setRentalDateTime(prev => ({ ...prev, date: newDate, time: '' }));

    // 반납 날짜도 동일하게 업데이트 및 시간 초기화
    setReturnDateTime(prev => ({ ...prev, date: newDate, time: '' }));

    // 결제 폼 초기화
    resetPaymentForm();


    const params = {
      ...createRequestParams(rentalState, returnState,
        { date: newDate, time: '' },
        { date: newDate, time: '' },
        selectedProductIdsRef.current
      )
    };

    orderApi.getCartDetail(params)
      .then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          setCartListData(prevCart => ({
            bundleMain: data.cartList?.bundleMain?.map(item => ({
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || [],
            bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || [],
            bundleAcc: data.cartList?.bundleAcc?.map(item => ({
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || []
          }));
          setOrderSummary(data); // 주문 요약 정보 업데이트
        }
      })
      .catch(error => {
        console.error('대여 날짜 변경 중 오류:', error);
    });
  };

  // 반납 날짜 변경 핸들러 수정
  const handleReturnDateChange = (newDate) => {
    // 반납 날짜 업데이트 및 시간 초기화
    setReturnDateTime(prev => ({ ...prev, date: newDate, time: '' }));

    // 결제 폼 초기화
    resetPaymentForm();


    const params = {
      ...createRequestParams(rentalState, returnState,
        rentalDateTime,
        { date: newDate, time: '' },
        selectedProductIdsRef.current
      )
    };

    orderApi.getCartDetail(params)
      .then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          setCartListData(prevCart => ({
            bundleMain: data.cartList?.bundleMain?.map(item => ({
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || [],
            bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || [],
            bundleAcc: data.cartList?.bundleAcc?.map(item => ({
              ...item,
              isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
            })) || []
          }));
          setOrderSummary(data); // 주문 요약 정보 업데이트
        }
      })
      .catch(error => {
        console.error('반납 날짜 변경 중 오류:', error);
    });
  };

  // 패키지 보기 버튼 핸들러 수정
  const handleShow = (cart_option_seq) => {
    setIsShow(prev => ({
      ...prev,
      [cart_option_seq]: !prev[cart_option_seq]
    }));
  };

  //요청사항
  const handleMemoChange = (e) => {
    setMemo(e.target.value);
  };

  const handleAllCheck = () => {
    setIsAllChecked(!isAllChecked);
    const newCheckedState = {
      option1: !isAllChecked,
      option2: !isAllChecked,
      option3: !isAllChecked,
      option4: !isAllChecked,
    };
    setIsChecked(newCheckedState);
  };

  const handleCheck = (option) => {
    const newCheckedState = { ...isChecked, [option]: !isChecked[option] };
    setIsChecked(newCheckedState);
    setIsAllChecked(Object.values(newCheckedState).every(Boolean));
  };

  // 초기 데이터 로딩을 위한 useEffect 수정
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const params = {
          order_kind: "visit",
          rental_store_seq: rentalState.selectValue || "",
          return_store_seq: returnState.selectValue || "",
          rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')}`,
          rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')}`,
          additional_sale: "",
          point: "",
          allPointUse: "N",
          download_seq: "",
          balance_price: "",
          allBalanceUse: "N",
          cart_select_goods: [],
          pay_select_goods: [],
          undecidedChk: "N"
        };

        const response = await orderApi.getCartDetail(params);
        if (response.data.code === 200) {
          const data = response.data.data;

          // 모든 상품의 cart_option_seq 수집
          const main = data.cartList?.bundleMain || [];
          const add = data.cartList?.bundleCp || [];
          const acc = data.cartList?.bundleAcc || [];

          const mainIds = main.map(item => item.cart_option_seq);
          // const accIds = acc.map(item => item.cart_option_seq);

          // 대여상품과 액세서리만 selectedProductIds에 설정
          const selectedProductIds = [...mainIds];
          setSelectedProductIds(selectedProductIds);

          // cartListData 설정 시 부가품목만 체크 해제
          setCartListData({
            bundleMain: main.map(item => ({
              ...item,
              isChecked: true
            })),
            bundleCp: add.map(item => ({
              ...item,
              isChecked: false // 부가품목만 디폴트로 체크 해제
            })),
            bundleAcc: acc.map(item => ({
              ...item,
              isChecked: false
            }))
          });

          // 선택된 개수 설정
          setSelectedCounts({
            main: main.length,
            add: 0, // 부가품목은 0개로 시작
            acc: 0
          });

          // 주문 요약 정보 설정
          setOrderSummary(data);

          didSetInitialSelection.current = true;

          // 초기 데이터 로드 후 API 재호출
          const updatedParams = {
            ...params,
            cart_select_goods: selectedProductIds
          };

          await orderApi.getCartDetail(updatedParams);
        }
      } catch (error) {
        console.error('초기 데이터 로딩 중 오류:', error);
      }
    };

    if (!didSetInitialSelection.current) {
      fetchInitialData();
    }
  }, [rentalState.selectValue, returnState.selectValue, rentalDateTime.date, returnDateTime.date]);

  const handleProductSelect = useCallback((productId, isSelected, type = 'main') => {
    // 대여상품(bundleMain) 관련 처리
    if (type === 'main') {
      // 체크/해제된 대여상품의 cart_seq 찾기
      const mainItem = cartListData.bundleMain.find(item => item.cart_option_seq === productId);

      if (mainItem && mainItem.cart_seq) {
        const mainCartSeq = mainItem.cart_seq;

        // 해당 cart_seq를 가진 부가품목과 액세서리 찾기
        const relatedCpItems = cartListData.bundleCp.filter(item => item.cart_seq === mainCartSeq);
        const relatedAccItems = cartListData.bundleAcc.filter(item => item.cart_seq === mainCartSeq);

        // 관련 부가품목과 액세서리의 cart_option_seq 목록 생성
        const relatedIds = [
          ...relatedCpItems.map(item => item.cart_option_seq), // bundleAdd에서 bundleCp로 변경
          ...relatedAccItems.map(item => item.cart_option_seq)
        ];

        // UI 업데이트 - 대여상품과 관련 부가품목/액세서리 모두 반영
        setCartListData(prevCart => ({
          bundleMain: prevCart.bundleMain.map(item => ({
            ...item,
            isChecked: item.cart_option_seq === productId ? isSelected : item.isChecked
          })),
          bundleCp: prevCart.bundleCp.map(item => ({ // bundleAdd에서 bundleCp로 변경
            ...item,
            // 관련 아이템일 경우 대여상품과 동일한 체크 상태로 설정
            isChecked: relatedIds.includes(item.cart_option_seq) ? isSelected : item.isChecked
          })),
          bundleAcc: prevCart.bundleAcc.map(item => ({
            ...item,
            // 관련 아이템일 경우 대여상품과 동일한 체크 상태로 설정
            isChecked: relatedIds.includes(item.cart_option_seq) ? isSelected : item.isChecked
          }))
        }));

        // selectedProductIds 업데이트
        setSelectedProductIds(prev => {
          let updated;

          if (isSelected) {
            // 체크 시: 대여상품과 관련 아이템 모두 추가
            updated = [...new Set([...prev, productId, ...relatedIds])];
          } else {
            // 체크 해제 시: 대여상품과 관련 아이템 모두 제거
            const idsToRemove = [productId, ...relatedIds];
            updated = prev.filter(id => !idsToRemove.includes(id));
          }

          // API 호출
          const params = createRequestParams(rentalState, returnState, rentalDateTime, returnDateTime, updated);
          debouncedFn(() => {
            orderApi.getCartDetail(params)
              .then(response => {
                if (response.data.code === 200) {
                  setOrderSummary(response.data.data);
                }
              })
              .catch(error => {
                console.error('상품 선택 업데이트 중 오류:', error);
              });
          });

          return updated;
        });

        return; // 여기서 함수 종료
      }
    }

    // 기존 부가품목/액세서리 체크 로직은 유지
    if ((type === 'add' || type === 'acc') && isSelected) {
      const item = type === 'add'
        ? cartListData.bundleCp.find(item => item.cart_option_seq === productId) // bundleAdd에서 bundleCp로 변경
        : cartListData.bundleAcc.find(item => item.cart_option_seq === productId);

      if (item && item.cart_seq) {
        const relatedMainItems = cartListData.bundleMain.filter(mainItem =>
          mainItem.cart_seq === item.cart_seq
        );

        const isAnyMainItemChecked = relatedMainItems.some(mainItem => mainItem.isChecked);

        if (!isAnyMainItemChecked) {
          setIsPopupLoginError('관련 대여상품을 먼저 선택해주세요.');
          return;
        }
      }
    }

    // 그 외 일반적인 경우 처리 (기존 코드와 동일)
    setCartListData(prevCart => ({
      bundleMain: prevCart.bundleMain?.map(item => ({
        ...item,
        isChecked: item.cart_option_seq === productId ? isSelected : item.isChecked
      })) || [],
      bundleCp: prevCart.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
        ...item,
        isChecked: item.cart_option_seq === productId ? isSelected : item.isChecked
      })) || [],
      bundleAcc: prevCart.bundleAcc?.map(item => ({
        ...item,
        isChecked: item.cart_option_seq === productId ? isSelected : item.isChecked
      })) || []
    }));

    setSelectedProductIds(prev => {
      const updated = isSelected
        ? [...new Set([...prev, productId])]
        : prev.filter(id => id !== productId);

      const params = createRequestParams(rentalState, returnState, rentalDateTime, returnDateTime, updated);
      debouncedFn(() => {
        orderApi.getCartDetail(params)
          .then(response => {
            if (response.data.code === 200) {
              setOrderSummary(response.data.data);
            }
          })
          .catch(error => {
            console.error('상품 선택 업데이트 중 오류:', error);
          });
      });

      return updated;
    });
  }, [rentalState, returnState, rentalDateTime, returnDateTime, cartListData]);

  // 상품 삭제 핸들러 수정
  const handleProductDelete = (deleteData) => {
    // 삭제될 상품 ID
    const deletedProductId = deleteData.cart_option_seq;

    // UI에서 먼저 삭제 (즉시 삭제 효과를 보여주기 위함)
    setCartListData(prev => ({
      bundleMain: prev.bundleMain?.filter(item => item.cart_option_seq !== deletedProductId) || [],
      bundleCp: prev.bundleCp?.filter(item => item.cart_option_seq !== deletedProductId) || [],
      bundleAcc: prev.bundleAcc?.filter(item => item.cart_option_seq !== deletedProductId) || []
    }));

    // selectedProductIds에서도 삭제
    const updatedProductIds = selectedProductIds.filter(id => id !== deletedProductId);
    setSelectedProductIds(updatedProductIds);

    // ref도 즉시 업데이트
    selectedProductIdsRef.current = updatedProductIds;

    // 체크박스 상태 초기화 - requestParams에 forceReset 플래그 추가
    setRequestParams(prev => ({
      ...prev,
      point: '',
      allPointUse: 'N',
      balance: '',
      allBalanceUse: 'N',
      maxCouponUse: 'N',
      download_seq: '',
      forceReset: true,
      // key: Date.now()
    }));

    // API 호출 파라미터
    const params = {
      ...createRequestParams(rentalState, returnState, rentalDateTime, returnDateTime, updatedProductIds),
      cart_change_goods: [deleteData],
      point: '',
      allPointUse: 'N',
      balance: '',
      allBalanceUse: 'N',
      maxCouponUse: 'N',
      download_seq: ''
    };

    // API 호출
    orderApi.getCartDetail(params)
      .then(response => {
        // 장바구니가 비어있는 경우 또는 성공적으로 응답했지만 상품 목록이 없는 경우
        if (response.data.code === -200 ||
            (response.data.code === 200 &&
             (!response.data.data.cartList ||
              Object.keys(response.data.data.cartList).length === 0 ||
              (!response.data.data.cartList.bundleMain?.length &&
               !response.data.data.cartList.bundleCp?.length &&
               !response.data.data.cartList.bundleAcc?.length)))) {
          // 장바구니가 비었으므로 모든 데이터 초기화
          setCartListData({
            bundleMain: [],
            bundleCp: [],
            bundleAcc: []
          });
          setSelectedProductIds([]);
          selectedProductIdsRef.current = [];
          setOrderSummary({...response.data.data, cartList: {bundleMain: [], bundleCp: [], bundleAcc: []}});

          // 주문 개수 업데이트
          refreshOrderCount();
          return;
        }

        if (response.data.code === 200) {
          const data = response.data.data;

          // 서버 데이터로 상태 업데이트
          setCartListData({
            bundleMain: data.cartList?.bundleMain?.map(item => ({
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || [],
            bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || [],
            bundleAcc: data.cartList?.bundleAcc?.map(item => ({
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || []
          });

          // 주문 요약 정보 업데이트
          setOrderSummary(data);

          // 주문 개수 업데이트
          refreshOrderCount();
        }
      })
      .catch(error => {
        console.error('상품 삭제 중 오류:', error);
        // 오류 발생 시 UI 초기화
        setCartListData({
          bundleMain: [],
          bundleCp: [],
          bundleAcc: []
        });
        setSelectedProductIds([]);
        selectedProductIdsRef.current = [];

        // 주문 개수 업데이트
        refreshOrderCount();
      });
  };

  // 대여상품만 삭제하는 핸들러 수정
  const handleMainProductsDelete = () => {
    // 대여상품(bundleMain) 중에서 선택된 항목만 필터링
    const mainProductIds = cartListData.bundleMain
      .filter(item => item.isChecked)
      .map(item => item.cart_option_seq);

    if (mainProductIds.length === 0) {
      setIsPopupLoginError('삭제할 대여상품을 선택해주세요.');
      return;
    }

    // cartListData에서 모든 상품을 하나의 배열로 합치기
    const allProducts = [
      ...(cartListData.bundleMain || []),
      ...(cartListData.bundleCp || []),
      ...(cartListData.bundleAcc || [])
    ];

    const deleteList = mainProductIds.map(id => {
      // cartListData에서 해당 cart_option_seq를 가진 상품 찾기
      const product = allProducts.find(item => item.cart_option_seq === id);
      // 상품을 찾으면 해당 ea 값 사용, 없으면 1 (기본값)
      const ea = product?.ea || 1;
      
      return {
        cart_option_seq: id,
        ea: ea,
        delete_flag: 'Y'
      };
    });

    const params = {
      ...createRequestParams(rentalState, returnState, rentalDateTime, returnDateTime, selectedProductIdsRef.current),
      cart_change_goods: deleteList
    };

    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(res => {
          // 장바구니가 비어있는 경우(코드가 -200) 또는 성공적으로 응답했지만 상품 목록이 없는 경우
          if (res.data.code === -200 ||
              (res.data.code === 200 &&
               (!res.data.data.cartList ||
                Object.keys(res.data.data.cartList).length === 0 ||
                (!res.data.data.cartList.bundleMain?.length &&
                 !res.data.data.cartList.bundleCp?.length &&
                 !res.data.data.cartList.bundleAcc?.length)))) {
            // 장바구니가 비었으므로 모든 데이터 초기화
            setCartListData({
              bundleMain: [],
              bundleCp: [],
              bundleAcc: []
            });
            setSelectedProductIds([]);
            selectedProductIdsRef.current = [];
            setOrderSummary({...res.data.data, cartList: {bundleMain: [], bundleCp: [], bundleAcc: []}});

            // 주문 개수 업데이트
            refreshOrderCount();
            return;
          }

          if (res.data.code === 200) {
            const data = res.data.data;
            // 삭제된 ID를 selectedProductIds에서 제거
            setSelectedProductIds(prev => prev.filter(id => !mainProductIds.includes(id)));

            // 새 데이터로 UI 업데이트
            setCartListData({
              bundleMain: data.cartList.bundleMain?.map(item => ({
                ...item,
                isChecked: selectedProductIds.filter(id => !mainProductIds.includes(id)).includes(item.cart_option_seq)
              })) || [],
              bundleCp: data.cartList.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: selectedProductIds.filter(id => !mainProductIds.includes(id)).includes(item.cart_option_seq)
              })) || [],
              bundleAcc: data.cartList.bundleAcc?.map(item => ({
                ...item,
                isChecked: selectedProductIds.filter(id => !mainProductIds.includes(id)).includes(item.cart_option_seq)
              })) || []
            });
            setOrderSummary(data);

            // 주문 개수 업데이트
            refreshOrderCount();
          }
        })
        .catch(err => {
          console.error('대여상품 삭제 실패', err);
          // 오류 발생 시 UI 초기화 (사용자 경험 향상을 위해)
          setCartListData({
            bundleMain: [],
            bundleCp: [],
            bundleAcc: []
          });
          setSelectedProductIds([]);
          selectedProductIdsRef.current = [];

          // 주문 개수 업데이트
          refreshOrderCount();
        });
    });
  };

  // 부가품목만 삭제하는 핸들러와 액세서리만 삭제하는 핸들러도 동일하게 수정
  const handleAddProductsDelete = () => {
    // 부가품목(bundleCp) 중에서 선택된 항목만 필터링
    const addProductIds = cartListData.bundleCp
      .filter(item => item.isChecked)
      .map(item => item.cart_option_seq);

    if (addProductIds.length === 0) {
      setIsPopupLoginError('삭제할 부가품목을 선택해주세요.');
      return;
    }

    const deleteList = addProductIds.map(id => ({
      cart_option_seq: id,
      ea: 0,
      delete_flag: 'Y'
    }));

    const params = {
      ...createRequestParams(rentalState, returnState, rentalDateTime, returnDateTime, selectedProductIdsRef.current),
      cart_change_goods: deleteList
    };

    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(res => {
          // 장바구니가 비어있는 경우(코드가 -200) 또는 성공적으로 응답했지만 상품 목록이 없는 경우
          if (res.data.code === -200 ||
              (res.data.code === 200 &&
               (!res.data.data.cartList ||
                Object.keys(res.data.data.cartList).length === 0 ||
                (!res.data.data.cartList.bundleMain?.length &&
                 !res.data.data.cartList.bundleCp?.length &&
                 !res.data.data.cartList.bundleAcc?.length)))) {
            // 장바구니가 비었으므로 모든 데이터 초기화
            setCartListData({
              bundleMain: [],
              bundleCp: [],
              bundleAcc: []
            });
            setSelectedProductIds([]);
            selectedProductIdsRef.current = [];
            setOrderSummary({...res.data.data, cartList: {bundleMain: [], bundleCp: [], bundleAcc: []}});

            // 주문 개수 업데이트
            refreshOrderCount();
            return;
          }

          if (res.data.code === 200) {
            const data = res.data.data;
            // 삭제된 ID를 selectedProductIds에서 제거
            setSelectedProductIds(prev => prev.filter(id => !addProductIds.includes(id)));

            // 새 데이터로 UI 업데이트
            setCartListData({
              bundleMain: data.cartList.bundleMain?.map(item => ({
                ...item,
                isChecked: selectedProductIds.filter(id => !addProductIds.includes(id)).includes(item.cart_option_seq)
              })) || [],
              bundleCp: data.cartList.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: selectedProductIds.filter(id => !addProductIds.includes(id)).includes(item.cart_option_seq)
              })) || [],
              bundleAcc: data.cartList.bundleAcc?.map(item => ({
                ...item,
                isChecked: selectedProductIds.filter(id => !addProductIds.includes(id)).includes(item.cart_option_seq)
              })) || []
            });
            setOrderSummary(data);

            // 주문 개수 업데이트
            refreshOrderCount();
          }
        })
        .catch(err => {
          console.error('부가품목 삭제 실패', err);
          // 오류 발생 시 UI 초기화 (사용자 경험 향상을 위해)
          setCartListData({
            bundleMain: [],
            bundleCp: [],
            bundleAcc: []
          });
          setSelectedProductIds([]);
          selectedProductIdsRef.current = [];

          // 주문 개수 업데이트
          refreshOrderCount();
        });
    });
  };

  const handleAccProductsDelete = () => {
    // 액세서리(bundleAcc) 중에서 선택된 항목만 필터링
    const accProductIds = cartListData.bundleAcc
      .filter(item => item.isChecked)
      .map(item => item.cart_option_seq);

    if (accProductIds.length === 0) {
      setIsPopupLoginError('삭제할 액세서리를 선택해주세요.');
      return;
    }

    const deleteList = accProductIds.map(id => ({
      cart_option_seq: id,
      ea: 0,
      delete_flag: 'Y'
    }));

    const params = {
      ...createRequestParams(rentalState, returnState, rentalDateTime, returnDateTime, selectedProductIdsRef.current),
      cart_change_goods: deleteList
    };

    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(res => {
          // 장바구니가 비어있는 경우(코드가 -200) 또는 성공적으로 응답했지만 상품 목록이 없는 경우
          if (res.data.code === -200 ||
              (res.data.code === 200 &&
               (!res.data.data.cartList ||
                Object.keys(res.data.data.cartList).length === 0 ||
                (!res.data.data.cartList.bundleMain?.length &&
                 !res.data.data.cartList.bundleCp?.length &&
                 !res.data.data.cartList.bundleAcc?.length)))) {
            // 장바구니가 비었으므로 모든 데이터 초기화
            setCartListData({
              bundleMain: [],
              bundleCp: [],
              bundleAcc: []
            });
            setSelectedProductIds([]);
            selectedProductIdsRef.current = [];
            setOrderSummary({...res.data.data, cartList: {bundleMain: [], bundleCp: [], bundleAcc: []}});

            // 주문 개수 업데이트
            refreshOrderCount();
            return;
          }

          if (res.data.code === 200) {
            const data = res.data.data;
            // 삭제된 ID를 selectedProductIds에서 제거
            setSelectedProductIds(prev => prev.filter(id => !accProductIds.includes(id)));

            // 새 데이터로 UI 업데이트
            setCartListData({
              bundleMain: data.cartList.bundleMain?.map(item => ({
                ...item,
                isChecked: selectedProductIds.filter(id => !accProductIds.includes(id)).includes(item.cart_option_seq)
              })) || [],
              bundleCp: data.cartList.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: selectedProductIds.filter(id => !accProductIds.includes(id)).includes(item.cart_option_seq)
              })) || [],
              bundleAcc: data.cartList.bundleAcc?.map(item => ({
                ...item,
                isChecked: selectedProductIds.filter(id => !accProductIds.includes(id)).includes(item.cart_option_seq)
              })) || []
            });
            setOrderSummary(data);

            // 주문 개수 업데이트
            refreshOrderCount();
          }
        })
        .catch(err => {
          console.error('액세서리 삭제 실패', err);
          // 오류 발생 시 UI 초기화 (사용자 경험 향상을 위해)
          setCartListData({
            bundleMain: [],
            bundleCp: [],
            bundleAcc: []
          });
          setSelectedProductIds([]);
          selectedProductIdsRef.current = [];

          // 주문 개수 업데이트
          refreshOrderCount();
        });
    });
  };

  // handleProductQuantityChange 함수 추가
  const handleProductQuantityChange = (changeData) => {
    const params = {
      ...createRequestParams(rentalState, returnState, rentalDateTime, returnDateTime, selectedProductIdsRef.current),
      cart_change_goods: [changeData]
    };

    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            setCartListData({
              bundleMain: data.cartList.bundleMain?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleCp: data.cartList.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleAcc: data.cartList.bundleAcc?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
            });
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('상품 수량 변경 중 오류:', error);
        });
    });
  };

  // StoreOperationTime 컴포넌트 정의
  const StoreOperationTime = ({ storeList }) => {
    return (
      <div className="operation_time">
        {storeList.map((store) => (
          <p key={store.store_seq} >
            {store.store_name}
            <span>
              {store.opening_hour_set === 'Y'
                ? `${formatTime(store.opening_start_hour, store.opening_start_min)}~${formatTime(store.opening_end_hour, store.opening_end_min)}`
                : '24시간 운영'}
            </span>
          </p>
        ))}
      </div>
    );
  };

  // selectedCounts 업데이트 로직 추가
  useEffect(() => {
    const countCheckedItems = (items = []) => items.filter(item => item.isChecked).length;

    setSelectedCounts({
      main: countCheckedItems(cartListData.bundleMain),
      add: countCheckedItems(cartListData.bundleCp), // bundleAdd에서 bundleCp로 변경
      acc: countCheckedItems(cartListData.bundleAcc)
    });
  }, [cartListData]);

  const handleDelete = (cartOptionSeq) => {
    const deleteData = {
      cart_option_seq: cartOptionSeq,
      ea: 0,
      delete_flag: 'Y'
    };

    handleProductDelete(deleteData); // 실제 API 호출 핸들러
  };

  // requestParams 상태 정의
  const [requestParams, setRequestParams] = useState({
    point: "",
    allPointUse: "N",
    balance: "",
    allBalanceUse: "N",
    balance_price: "",
    // key: Date.now() // 고유 키 추가
  });

  // handleUpdateParams 정의 - 수정된 버전
  const handleUpdateParams = useCallback((updates) => {
    setRequestParams(prev => {
      const newParams = {
        ...prev,
        ...updates
      };

      // API 호출을 위한 파라미터 생성
      const params = {
        ...createRequestParams(rentalState, returnState, rentalDateTime, returnDateTime, selectedProductIdsRef.current),
        ...newParams
      };

      debouncedFn(() => {
        orderApi.getCartDetail(params)
          .then(response => {
            if (response.data.code === 200) {
              const data = response.data.data;

              // 체크박스 상태 유지하면서 데이터 업데이트
              setCartListData(prevCart => {
                // bundleMain 업데이트 - 기존 체크 상태 유지
                const bundleMain = data.cartList?.bundleMain?.map(item => {
                  const existingItem = prevCart.bundleMain?.find(
                    prevItem => prevItem.cart_option_seq === item.cart_option_seq
                  );
                  return {
                    ...item,
                    isChecked: existingItem ? existingItem.isChecked : true
                  };
                }) || [];

                // bundleCp 업데이트 - 기존 체크 상태 유지
                const bundleCp = data.cartList?.bundleCp?.map(item => {
                  const existingItem = prevCart.bundleCp?.find(
                    prevItem => prevItem.cart_option_seq === item.cart_option_seq
                  );
                  return {
                    ...item,
                    isChecked: existingItem ? existingItem.isChecked : true
                  };
                }) || [];

                // bundleAcc 업데이트 - 기존 체크 상태 유지
                const bundleAcc = data.cartList?.bundleAcc?.map(item => {
                  const existingItem = prevCart.bundleAcc?.find(
                    prevItem => prevItem.cart_option_seq === item.cart_option_seq
                  );
                  return {
                    ...item,
                    isChecked: existingItem ? existingItem.isChecked : true
                  };
                }) || [];

                return { bundleMain, bundleCp, bundleAcc };
              });

              setOrderSummary(data);
            }
          })
          .catch(error => {
            console.error('포인트/예치금/쿠폰 적용 중 오류:', error);
          });
      });

      return newParams;
    });
  }, [rentalState, returnState, rentalDateTime, returnDateTime]);

  // 결제 관련 state 추가
  const [isPayment, setIsPayment] = useState(false);

  // 결제 시기 상태 추가
  const [paymentType, setPaymentType] = useState('visit'); // 기본값 'online'

  // 결제 전 유효성 검사
  const orderInfoValidation = () => {
    const validation = PaymentService.validatePaymentForm({
      // 대여 기간 정보만 검증
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`
    });

    if (!validation.isValid) {
      setIsPopupLoginError(validation.errors[0]);
      return false;
    }

    return true;
  };

  // handlePayment 함수 수정
  const handlePayment = async () => {
    // 대여 시간과 반납 시간 검증 추가
    if (!rentalDateTime.time) {
      setIsPopupLoginError('대여 시간을 선택해주세요.');
      return;
    }

    if (!returnDateTime.time) {
      setIsPopupLoginError('반납 시간을 선택해주세요.');
      return;
    }

    // 선택된 상품이 있는지 확인
    if (!selectedProductIds || selectedProductIds.length === 0) {
      setIsPopupLoginError('결제할 상품을 선택해주세요.');
      return;
    }

    // 필수 약관 동의 확인
    if (!Object.values(isChecked).every(Boolean)) {
      setIsPopupLoginError('필수 약관에 모두 동의해주세요.');
      return;
    }

    const isValid = orderInfoValidation();
    if (!isValid) return;

    // 결제 요청 데이터 준비
    const paymentForm = {
      order_seq: "",
      reorder_chk: "N",
      order_type: "visit",
      rental_store_seq: rentalState.selectValue,
      return_store_seq: returnState.selectValue,
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      download_seq: requestParams.maxCouponUse === 'Y'
      ? orderSummary?.totalInfo?.maxDiscountCouponSeq
      : requestParams.download_seq || "",
      point: requestParams.point || "",
      balance: requestParams.balance || "",
      shipping_cost: 0, // 방문은 배송비 없음
      deposit_price: orderSummary?.totalInfo?.deposit_price || 0,
      balance_cash_receipt: requestParams.deposit_cash_receipt || 'N',
      balance_cash_num: requestParams.deposit_cash_num || '',

      depositInfo: {
        deposit_refund_account: "",
        deposit_refund_mname: "",
        deposit_refund_name: "",
        deposit_refund_price: orderSummary?.totalInfo?.deposit_price || 0
      },

      discountInfo: {
        first_payment_sale: orderSummary?.totalInfo?.first_payment_sale || 0, // 추가할인
        mem_group_sale: orderSummary?.totalInfo?.sumGroupSale || 0, // 회원 등급 할인
        mem_type_sale: orderSummary?.totalInfo?.sumMtypeSale || 0 // 회원 유형 할인
      },

      insuranceInfo: {
        insurance_seq: requestParams.insurance_seq || 0,
        insurance_repair_per: orderSummary?.insuranceList?.find(
          insurance => insurance.insurance_seq === requestParams.insurance_seq
        )?.insurance_repair_per || 0,
        insurance_price: orderSummary?.totalInfo?.insurance_price || 0
      },

      user_memo: memo,
      pay_select_goods: selectedProductIds.filter(id => {
        const isMainChecked = cartListData.bundleMain?.some(item =>
          item.cart_option_seq === id && item.isChecked);
        const isAddChecked = cartListData.bundleCp?.some(item =>
          item.cart_option_seq === id && item.isChecked);
        const isAccChecked = cartListData.bundleAcc?.some(item =>
          item.cart_option_seq === id && item.isChecked);
        return isMainChecked || isAddChecked || isAccChecked;
      }),
      sale_order_kind: paymentType, // 결제 시기 추가
    };

    try {
      const result = await PaymentService.requestPayment(paymentForm);


      if (result.success) {
        const data = result.data;

        // 0원 결제 처리(매장 방문)
        if (data.payment_pending === "Y") {
          const zeroPaymentData = PaymentService.createZeroPaymentData(data);
          setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
          navigate(`/order/orderComplete/${zeroPaymentData.orderSeq}`, {
            state: {
              order_seq: zeroPaymentData.orderSeq,
              settleprice: zeroPaymentData.settleprice,
              order_kind: zeroPaymentData.orderKind,
              order_status: "free",
              order_data: data,
            }
          });
          return;
        }

        if (result.isMobile) {
          // 모바일에서는 새 창에서 결제 처리
          const paymentWindow = PaymentService.processMobilePayment(data, window.location.origin);
          if (!paymentWindow) {
            setIsPopupLoginError('결제창을 열 수 없습니다. 팝업 차단을 확인해주세요.');
          }
        } else {
          setIsPayment(true);

          setTimeout(() => {
            const iframe = document.getElementById('payment-container');
            if (iframe) {
              const paymentUrl = PaymentService.createPaymentUrl(data, window.location.origin);
              iframe.src = paymentUrl;
            } else {
              console.error('결제창 iframe을 찾을 수 없습니다.');
              setIsPayment(false);
            }
          }, 100);
        }
      } else {
        // 결제 실패 시 재고부족 팝업 처리

        if (result.data?.code === 500 && result.data?.data && result.data.data.length > 0) {
          setStockData(result.data); // 전체 데이터 전달
          setStockPopup(true);
          return;
        } else {
          setIsPopupLoginError(result?.data?.message || '결제 요청에 실패했습니다.');
        }
        setIsPayment(false);
      }
    } catch (error) {
      console.error('결제 요청 중 오류:', error);
      setIsPopupLoginError('결제 처리 중 오류가 발생했습니다.');
      setIsPayment(false);
    }
  };

  // 결제 완료 메시지 처리를 위한 useEffect 추가
  useEffect(() => {
    const handlePaymentMessage = (e) => {
      const result = PaymentService.handlePaymentMessage(e);

      if (!result) return;

      if (result.success) {
        setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
        navigate(`/order/orderComplete/${result.orderSeq}`, {
          state: {
            order_seq: result.orderSeq,
            settleprice: result.settleprice,
            order_kind: result.orderKind
          }
        });
      } else {
        setIsPopupLoginError(result.message || '주문이 실패하였습니다.');
      }
    };

    window.addEventListener('message', handlePaymentMessage);
    return () => window.removeEventListener('message', handlePaymentMessage);
  }, [navigate]);

  // 컴포넌트 마운트 시 모바일 결제 결과 확인
  useEffect(() => {
    const paymentResult = PaymentService.checkMobilePaymentResult();
    if (paymentResult) {
      if (paymentResult.success) {
        setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
        navigate(`/order/orderComplete/${paymentResult.orderSeq}`, {
          state: {
            order_seq: paymentResult.orderSeq,
            settleprice: paymentResult.settleprice,
            order_kind: paymentResult.orderKind
          }
        });
      } else {
        setIsPopupLoginError(paymentResult.message || '주문이 실패하였습니다.');
      }
    }
  }, [navigate]);


  // 결제 시기 선택 핸들러 추가
  const handlePaymentTypeChange = (e) => {
    setPaymentType(e.target.value);
  };

  // 결제 폼 초기화 함수
  const [shouldResetPayment, setShouldResetPayment] = useState(false);
  const resetPaymentForm = () => {
    setShouldResetPayment(prev => !prev);
  };

  // 초기화
  const handleReset = () => {
    window.location.reload();
  }

  // agree_flag 체크를 위한 useEffect (기존 fetchMyInfo API 호출 대신)
  useEffect(() => {
    if (memberTokens && userInfo?.agree_flag === 'R') {
      setIsPopupLoginError({
        message: t("authentication_info_expired"),
        action: 'navigate_profile_edit'
      });
    }
  }, [memberTokens, userInfo, t]);

  const handleConfirm = () => {
    if (isPopupLoginError.action === 'navigate_profile_edit') {
      navigate('/myPage/profile/edit');
    } else if (isPopupLoginError.action === 'navigate_login') {
      navigate('/login');
    } else {
      // window.location.reload();
    }
  }

  // Context 사용
  const { refreshOrderCount } = useOrderCount();

  // 재고부족 팝업 확인 핸들러 추가
  const handleStockPopupConfirm = (deleteCartOptionData) => {
    setStockPopup(false);

    // 재고부족 상품을 제외하고 결제 진행
    handlePaymentWithStockShortage(deleteCartOptionData);
  };

  // 재고부족이 있는 경우의 결제 처리 함수 추가
  const handlePaymentWithStockShortage = async (deleteCartOptionData) => {
    // 대여 시간과 반납 시간 검증 추가
    if (!rentalDateTime.time) {
      setIsPopupLoginError('대여 시간을 선택해주세요.');
      return;
    }

    if (!returnDateTime.time) {
      setIsPopupLoginError('반납 시간을 선택해주세요.');
      return;
    }

    // 선택된 상품이 있는지 확인
    if (!selectedProductIds || selectedProductIds.length === 0) {
      setIsPopupLoginError('결제할 상품을 선택해주세요.');
      return;
    }

    // 필수 약관 동의 확인
    if (!Object.values(isChecked).every(Boolean)) {
      setIsPopupLoginError('필수 약관에 모두 동의해주세요.');
      return;
    }

    const isValid = orderInfoValidation();
    if (!isValid) return;

    // 결제 요청 데이터 준비 (재고부족 관련 필드 추가)
    const paymentForm = {
      order_seq: "",
      reorder_chk: "N",
      order_type: "visit",
      rental_store_seq: rentalState.selectValue,
      return_store_seq: returnState.selectValue,
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      download_seq: requestParams.maxCouponUse === 'Y'
      ? orderSummary?.totalInfo?.maxDiscountCouponSeq
      : requestParams.download_seq || "",
      point: requestParams.point || "",
      balance: requestParams.balance || "",
      shipping_cost: 0, // 방문은 배송비 없음
      deposit_price: orderSummary?.totalInfo?.deposit_price || 0,
      balance_cash_receipt: requestParams.deposit_cash_receipt || 'N',
      balance_cash_num: requestParams.deposit_cash_num || '',

      depositInfo: {
        deposit_refund_account: "",
        deposit_refund_mname: "",
        deposit_refund_name: "",
        deposit_refund_price: orderSummary?.totalInfo?.deposit_price || 0
      },

      discountInfo: {
        first_payment_sale: orderSummary?.totalInfo?.first_payment_sale || 0, // 추가할인
        mem_group_sale: orderSummary?.totalInfo?.sumGroupSale || 0, // 회원 등급 할인
        mem_type_sale: orderSummary?.totalInfo?.sumMtypeSale || 0 // 회원 유형 할인
      },

      insuranceInfo: {
        insurance_seq: requestParams.insurance_seq || 0,
        insurance_repair_per: orderSummary?.insuranceList?.find(
          insurance => insurance.insurance_seq === requestParams.insurance_seq
        )?.insurance_repair_per || 0,
        insurance_price: orderSummary?.totalInfo?.insurance_price || 0
      },

      user_memo: memo,
      pay_select_goods: selectedProductIds.filter(id => {
        const isMainChecked = cartListData.bundleMain?.some(item =>
          item.cart_option_seq === id && item.isChecked);
        const isAddChecked = cartListData.bundleCp?.some(item =>
          item.cart_option_seq === id && item.isChecked);
        const isAccChecked = cartListData.bundleAcc?.some(item =>
          item.cart_option_seq === id && item.isChecked);
        return isMainChecked || isAddChecked || isAccChecked;
      }),
      sale_order_kind: paymentType, // 결제 시기 추가
      stock_shortage_flag: "Y", // 재고부족 플래그 추가
      delete_cart_option: deleteCartOptionData // 삭제할 상품 정보 추가
    };

    try {
      const result = await PaymentService.requestPayment(paymentForm);


      if (result.success) {
        const data = result.data;

        // 0원 결제 처리 (문자열 "0"과 숫자 0 모두 처리)
        if (Number(data.price) === 0) {
          const zeroPaymentData = PaymentService.createZeroPaymentData(data);
          setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
          navigate(`/order/orderComplete/${zeroPaymentData.orderSeq}`, {
            state: {
              order_seq: zeroPaymentData.orderSeq,
              settleprice: zeroPaymentData.settleprice,
              order_kind: zeroPaymentData.orderKind,
              order_status: "free",
            }
          });
          return;
        }

        if (result.isMobile) {
          // 모바일에서는 새 창에서 결제 처리
          const paymentWindow = PaymentService.processMobilePayment(data, window.location.origin);
          if (!paymentWindow) {
            setIsPopupLoginError('결제창을 열 수 없습니다. 팝업 차단을 확인해주세요.');
          }
        } else {
          setIsPayment(true);

          setTimeout(() => {
            const iframe = document.getElementById('payment-container');
            if (iframe) {
              const paymentUrl = PaymentService.createPaymentUrl(data, window.location.origin);
              iframe.src = paymentUrl;
            } else {
              console.error('결제창 iframe을 찾을 수 없습니다.');
              setIsPayment(false);
            }
          }, 100);
        }
      } else {
        setIsPopupLoginError(result?.data?.message || '결제 요청에 실패했습니다.');
        setIsPayment(false);
      }
    } catch (error) {
      console.error('재고부족 결제 요청 중 오류:', error);
      setIsPopupLoginError('결제 처리 중 오류가 발생했습니다.');
      setIsPayment(false);
    }
  };

  // 재고 부족 항목이 있는 패키지 자동 펼치기
  useEffect(() => {
    if (cartListData && (cartListData.bundleMain || cartListData.bundleCp || cartListData.bundleAcc)) {
      const newIsShow = {};

      // bundleMain에서 bundleCp와 bundlePkg를 확인
      if (cartListData.bundleMain && cartListData.bundleMain.length > 0) {
        cartListData.bundleMain.forEach(item => {
          let hasStockShortage = false;

          // bundleCp 중에서 stock_flag가 'N'인 항목이 있는지 확인
          if (item.bundleCp && item.bundleCp.length > 0) {
            hasStockShortage = item.bundleCp.some(cpItem => cpItem.stock_flag === 'N');
          }

          // bundlePkg 중에서 stock_flag가 'N'인 항목이 있는지 확인
          if (!hasStockShortage && item.bundlePkg && item.bundlePkg.length > 0) {
            hasStockShortage = item.bundlePkg.some(pkgItem => pkgItem.stock_flag === 'N');
          }

          if (hasStockShortage) {
            newIsShow[item.cart_option_seq] = true; // 재고 부족 항목이 있으면 펼치기
          }
        });
      }

      // 기존 상태와 병합 (기존에 수동으로 열린 항목들은 유지)
      setIsShow(prev => ({
        ...prev,
        ...newIsShow
      }));
    }
  }, [cartListData]);

  // 팝업 상태 추가
  const [isTermsPopup, setIsTermsPopup] = useState(false);
  const [popupContent, setPopupContent] = useState({ title: '', content: '' });

  // 약관 보기 버튼 클릭 핸들러 수정
  const handleViewTerms = async (type, event) => {
    event.stopPropagation(); // 이벤트 전파 중지
    event.preventDefault(); // 기본 동작 방지
    
    let title = '';
    let dataKey = '';

    switch(type) {
      case 'order_product_agreement':
        title = '주문 상품에 동의';
        dataKey = 'order_product_agreement';
        break;
      case 'privacy_use_agreement':
        title = '개인정보 수집/이용 동의';
        dataKey = 'privacy_use_agreement';
        break;
      case 'privacy_3rd_agreement':
        title = '개인정보 제3자 제공 동의';
        dataKey = 'privacy_3rd_agreement';
        break;
      case 'pg_service_agreement':
          title = '';
          dataKey = 'pg_service_agreement';
          break;
      default:
        return;
    }

    try {
      const response = await instance.post(TERMS_LIST_URL);
      
      if (response.data.code === 200) {
        setPopupContent({ 
          title, 
          content: response.data.data[dataKey] || '약관 내용을 불러올 수 없습니다.' 
        });
        setIsTermsPopup(true);
      } else {
        throw new Error(response.data.message || '약관 내용을 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('약관 내용을 불러오는 중 오류 발생:', error);
      setPopupContent({ 
        title, 
        content: '약관 내용을 불러올 수 없습니다. 잠시 후 다시 시도해주세요.' 
      });
      setIsTermsPopup(true);
    }
  };


  // 대표 상담번호 
  const [storePhone, setStorePhone] = useState('');
  useEffect(() => {
    const fetchStorePhone = async () => {
      try {
        const response = await instance.post(FOOTER_DATA_URL);
        setStorePhone(response.data.data.data);
      } catch (error) {
        console.error('대표 상담번호 가져오기 실패:', error);
      }
    };
    fetchStorePhone();
  }, []);


  return(
    <div>
      <div className="order_inner">

        {/* 대여 주문단계 아이콘 */}
        <div className="order_step">
          <ul>
            <li>
              <img src={o_select} alt="order_select" loading="lazy"/>
              <p>{t("equipment_selection")}</p>
            </li>
            <li className="step_icon">
              <img src={next} alt="next" loading="lazy"/>
            </li>
            <li>
              <img src={o_order} alt="o_order" loading="lazy"/>
              <p>{t("equipment_order")}</p>
            </li>
            <li className="step_icon">
              <img src={next} alt="next" loading="lazy"/>
            </li>
            <li>
              <img src={o_receive} alt="o_receive" loading="lazy"/>
              <p>{t("equipment_received")}</p>
            </li>
            <li className="step_icon">
              <img src={next} alt="next" loading="lazy"/>
            </li>
            <li>
              <img src={o_rental} alt="o_rental" loading="lazy"/>
              <p>{t("equipment_rented")}</p>
            </li>
            <li className="step_icon">
              <img src={next} alt="next" loading="lazy"/>
            </li>
            <li>
              <img src={o_return} alt="o_rental" loading="lazy"/>
              <p>{t("equipment_returned")}</p>
            </li>
          </ul>
        </div>

        {/* 방문 > 대여지점,대여일시  / 택배 > 대여기간 */}
        <div className="o_rental_time">
          <div className="order_title">
            <h3>{t("select_rental_branch_and_datetime")}</h3>
            <button className="reset_button" onClick={handleReset}>
              {t("initialization")}
            </button>
          </div>

            <div className="rental_box">
              {/* 대여 */}
              <div className="rental_select">
                  <div className="rental_label">{t("rental_branch_and_datetime")}</div>
                  <div className="select_info">
                    {/* 대여 지점 */}
                    <div
                      ref={rentalSelectRef}
                      className={`default_select border ${rentalState.isOpen ? 'open' : ''}`}
                    >
                      <button
                        type="button"
                        className={`select_btn ${rentalState.selectValue ? 'active' : ''}`}
                        onClick={() => setRentalState(prev => ({ ...prev, isOpen: !prev.isOpen }))}
                      >
                        <span className="ellipsis_clamp">
                          {rentalState.selectItem ? (
                            <span className="select_store">{rentalState.selectItem}</span>
                            ) : (
                            <span>{t("select_rental_branch")}</span>
                            )}
                        </span>
                      </button>
                      <ul className="select_list">
                        {storeList.map((store) => (
                          <li
                            key={store.store_seq}
                            className={`select_item ellipsis1 ${
                              rentalState.selectValue === store.store_seq ? 'active' : ''
                            }`}
                            onClick={() => handleRentalSelectItem(store)}
                          >
                            {store.store_name}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 대여 일시 */}
                    <DatePicker
                      value={rentalDateTime.date}
                      onDateChange={handleRentalDateChange}
                      maxDate={null}
                      minDate={dayjs()}
                      isDisabled={false}
                      disableHolidays={true}  // 공휴일만 비활성화
                      disableWeekends={false} // 주말은 활성화
                    />
                    <TimePicker
                      value={rentalDateTime.time}
                      onTimeChange={handleRentalTimeChange}
                      isDisabled={!rentalState.storeInfo}
                      storeInfo={rentalState.storeInfo}
                      compareDate={rentalDateTime.date}
                      isTodayCheck={true}
                    />
                  </div>
              </div>
              <span className="o_period">~</span>

              {/* 반납 */}
              <div className="return_select">
                <div className="select_box">
                  <p className="rental_label">{t("return_branch_and_datetime")}</p>
                  <div className="select_info">
                    {/* 반납 지점 선택 */}
                    <div
                      ref={returnSelectRef}
                      className={`default_select border ${returnState.isOpen ? 'open' : ''}`}
                    >
                      <button
                        className={`select_btn ${returnState.selectValue ? 'active' : ''}`}
                        type="button"
                        onClick={() => setReturnState(prev => ({ ...prev, isOpen: !prev.isOpen }))}
                      >
                        <span className="ellipsis_clamp">
                          {returnState.selectItem ? (
                            <span className="select_store">{returnState.selectItem}</span>
                          ) : (
                            <span>{t("select_return_branch")}</span>
                          )}
                        </span>
                      </button>
                      <ul className="select_list">
                        {storeList.map((store) => (
                          <li
                            key={store.store_seq}
                            className={`select_item ellipsis1 ${
                              returnState.selectValue === store.store_seq ? 'active' : ''
                            }`}
                            onClick={() => handleReturnSelectItem(store)}
                          >
                            {store.store_name}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 반납 일시 */}
                      <DatePicker
                        value={returnDateTime.date}
                        onDateChange={handleReturnDateChange}
                        minDate={rentalDateTime.date}
                        isDisabled={false}
                        disableHolidays={true}  // 공휴일만 비활성화
                        disableWeekends={false} // 주말은 활성화
                      />
                      <TimePicker
                        value={returnDateTime.time}
                        onTimeChange={handleReturnTimeChange}
                        isDisabled={!returnState.storeInfo}
                        storeInfo={returnState.storeInfo}
                        compareDate={returnDateTime.date}
                        compareTime={rentalDateTime.time}
                        isReturn={true}
                        isTodayCheck={true}
                        rentalDateTime={rentalDateTime}
                      />
                  </div>
                </div>
              </div>
            </div>

            {/*  영업시간 정보 표시 */}
            <StoreOperationTime storeList={storeList} />

            {/*  총 대여일 */}
            <div
              className="total_rental flex-center-2"
              style={{marginTop:"0"}}
            >
              <span>{t("total_rental_days2")}</span>
              <p><span className="strong_b1">{orderSummary?.totalInfo?.rental_period}</span>{t("total_rental_days_count_0417")}</p>
            </div>
        </div>


        {/* 대여상품 선택 */}
        <section className="mt_48 check_pd">
          <div className="order_title">
            <div className="flex-8">
              <h3>{t("select_rental_product_txt")}</h3>
              <p className="count">{selectedCounts.main}<span style={{color:"#8d8d8d"}}>/{cartListData?.bundleMain?.length || 0}</span></p>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleMainProductsDelete}
              >
                {t("delete_selected_product")}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleMain && cartListData.bundleMain.length > 0 ? (
                cartListData.bundleMain.map((item, index) => (
                  <li
                    key={item.cart_option_seq || index}
                    style={{
                      borderTop: index > 0 ? '1px solid black' : 'none'
                    }}
                  >
                    <OrderItem
                      item={item}
                      onSelect={(productId, isSelected) => handleProductSelect(productId, isSelected, 'main')}
                      onQuantityChange={handleProductQuantityChange}
                      onDelete={handleProductDelete}
                      openInNewWindow={true}
                    />

                    {/* bundlePkg가 있는 경우에만 패키지 내용물 표시 */}
                    {item.bundlePkg && item.bundlePkg.length > 0 && (
                      <div
                        className={`package_con ${isShow[item.cart_option_seq] ? 'active' : ''}`}
                        onClick={() => handleShow(item.cart_option_seq)}
                      >
                        <div className="package_total flex-justify-center">
                          {/* 패키지 전체보기 버튼 */}
                          <div className="flex-center-2">
                            {t("package")}<span className="strong_ch">{item.bundlePkg.length}</span>
                          </div>
                          {t("find_email_0617")}<i className="arrow_icon"></i>
                        </div>

                        {/* 패키지 내용물 매핑 */}
                        {item.bundlePkg.map((pkgItem, pkgIndex) => (
                          <div className="package_item" key={pkgItem.cart_option_seq || `pkg-${pkgIndex}`}>
                            <div className="default_chk_box"></div>
                            <div className="prod_container">
                              <img src={pkgItem.goods_image || product}
                                alt="상품 이미지"
                                className="order_prod_img"
                                loading="lazy"
                              />
                              <div className="prod_info">
                                <div className="info_box">
                                  <div className="prod_title">
                                    <p className="title ellipsis1">{pkgItem.product_name || 'ARRI ALEXA Mini Full'}</p>
                                    <p className="sub_title ellipsis1">{pkgItem.description_kr_01 || 'PL Mountd PL Mountd PL Mountd'}</p>
                                    <p className="option_count">× {pkgItem.ea}</p>
                                  </div>
                                </div>

                                <div className="prod_count_box">
                                  <div>{/* 빈박스 */}</div>
                                  {pkgItem?.stock_flag === 'N' && (
                                    <div className="stock_btn">
                                      <span className="mobile-text">{t('stock_shortage')}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="total_price">
                                  <button
                                    type="button"
                                    className="delete_btn"
                                    onClick={() => handleDelete(pkgItem.cart_option_seq)}
                                  ></button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                      </div>
                    )}

                      {/* bundleCp가 있는 경우에만 구성품 내용물 표시 */}
                      {item.bundleCp && item.bundleCp.length > 0 && (
                      <div
                        className={`package_con ${isShow[item.cart_option_seq] ? 'active' : ''}`}
                        onClick={() => handleShow(item.cart_option_seq)}
                      >
                        <div className="package_total flex-justify-center">
                          {/* 구성품 전체보기 버튼 */}
                          <div className="flex-center-2">
                            구성품<span className="strong_ch">{item.bundleCp.length}</span>
                          </div>
                          {t("find_email_0617")}<i className="arrow_icon"></i>
                        </div>

                        {/* 구성품 내용물 매핑 */}
                        {item.bundleCp.map((CpItem, pkgIndex) => (
                          <div className="package_item" key={CpItem.cart_option_seq || `pkg-${pkgIndex}`}>
                            <div className="default_chk_box"></div>
                            <div className="prod_container">
                              <img src={CpItem.goods_image || product}
                                alt="상품 이미지"
                                className="order_prod_img"
                                loading="lazy"
                              />
                              <div className="prod_info">
                                <div className="info_box">
                                  <div className="prod_title">
                                    <p className="title ellipsis1">{CpItem.product_name || 'ARRI ALEXA Mini Full'}</p>
                                    <p className="sub_title ellipsis1">{CpItem.description_kr_01 || 'PL Mountd PL Mountd PL Mountd'}</p>
                                    <p className="option_count">× {CpItem.ea}</p>
                                  </div>
                                </div>

                                <div className="prod_count_box">
                                  <div>{/* 빈박스 */}</div>
                                  {CpItem?.stock_flag === 'N' && (
                                    <div className="stock_btn">
                                      <span className="mobile-text">{t('stock_shortage')}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="total_price">
                                  <button
                                    type="button"
                                    className="delete_btn"
                                    onClick={() => handleDelete(CpItem.cart_option_seq)}
                                  ></button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                      </div>
                    )}

                  </li>
                ))
              ) : (
                <div className="no_result">
                  <p>{t("no_rental_product_0617")}</p>
                </div>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            <div className="total_product flex-center-8" style={{flexDirection:'column', alignItems:'end'}}>
              <div className="flex-center-8">

                <div className="flex-center-2">
                  {t("total_product_quantity2")}
                  <p><span className="strong_b1">{orderSummary?.totalInfo?.total_rental_ea || 0}</span>{t("find_email_0617")}</p>
                </div>

                <div className="flex-center-2">
                  {t("total_product_quantity4_0617")}
                  <p className="cap2_1">₩
                    <span className="strong_b1">
                      {formatLocalString(orderSummary?.totalInfo?.sumBundleMainPrice || 0)}
                    </span>
                  </p>
                </div>

              </div>
              <span className="text_small_b g-80">{t("payment_final_amount_notice")}</span>

            </div>
          </div>
        </section>


        {/* 부가품목 선택 */}
        {cartListData?.bundleCp && cartListData.bundleCp.length > 0 && (
        <section className="mt_48 check_pd">
          <div className="order_title">
            <div className="flex-8">
              <h3>구성품 선택</h3>
              <p className="count">{selectedCounts.add}<span style={{color:"#8d8d8d"}}>/{cartListData?.bundleCp?.length || 0}</span></p>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleAddProductsDelete}
              >
                {t("delete_selected_product")}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleCp && cartListData.bundleCp.length > 0 ? ( // bundleAdd에서 bundleCp로 변경
                cartListData.bundleCp.map((item, index) => ( // bundleAdd에서 bundleCp로 변경
                  <li key={item.cart_option_seq || index}>
                    <OrderItem
                      item={item}
                      onSelect={(productId, isSelected) => handleProductSelect(productId, isSelected, 'add')}
                      onQuantityChange={handleProductQuantityChange}
                      onDelete={handleProductDelete}
                    />
                  </li>
                ))
              ) : (
                <div className="no_result">
                  <p>{t("no_additional_items_0617")}</p>
                </div>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            <div className="total_product flex-center-8">
              <div className="flex-center-2">
                {t("total_product_quantity2")}
                <p><span className="strong_b1">{orderSummary?.totalInfo?.total_add_ea || 0}</span>{t("find_email_0617")}</p>
              </div>
              <div className="flex-center-2">
              {t("total_product_quantity4_0617")}
                <p className="cap2_1">₩<span className="strong_b1">
                  {formatLocalString(orderSummary?.totalInfo?.sumBundleAddPrice || 0)}
                </span></p>
                </div>
            </div>
          </div>
        </section>
        )}


        {/* 무상품목 선택 */}
        {cartListData?.bundleAcc && cartListData.bundleAcc.length > 0 && (
        <section className="mt_48 check_pd acc_section">
          <div className="order_title">
            <div className="flex-8">
              <h3>{t('acc_info_txt')}</h3>
              <p className="count">{selectedCounts.acc}<span style={{color:"#8d8d8d"}}>/{cartListData?.bundleAcc?.length || 0}</span></p>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleAccProductsDelete}
              >
                {t("delete_selected_product")}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleAcc && cartListData.bundleAcc.length > 0 ? (
                cartListData.bundleAcc.map((item, index) => (
                  <li key={item.cart_option_seq || index}>
                    <OrderItem
                      item={item}
                      onSelect={(productId, isSelected) => handleProductSelect(productId, isSelected, 'acc')}
                      onQuantityChange={handleProductQuantityChange}
                      onDelete={handleProductDelete}
                    />
              </li>
                ))
              ) : (
                <div className="no_result">
                  <p>{t("no_accessory_0617")}</p>
                </div>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            <div className="total_product flex-center-8">
              <div className="flex-center-2">
              {t("total_product_quantity2")}
                <p><span className="strong_b1">{orderSummary?.totalInfo?.total_acc_ea || 0}</span>{t("find_email_0617")}</p>
                </div>
            </div>
          </div>
        </section>
        )}


        {/* 결제금액 */}
        <section className="mt_48">
          <div className="order_title">
            <h3>{t("payment_amount")}</h3>
          </div>

          <div>
            <OrderPayment
              orderSummary={orderSummary}
              onUpdateParams={handleUpdateParams}
              requestParams={requestParams}
              orderType={"visit"}
              onReset={shouldResetPayment}
            />
          </div>
        </section>


        {/* 결제시기 > 새로 추가됨 > 방문 주문에서만 표출 */}
        <section className="mt_48 payment_period">
          <div className="order_title">
            <h3>{t("payment_timing")}</h3>
          </div>
          <div className="default_info_table payment">
            <div className="info_item total_order_price">
              <div className="label">
                <span>{t("select_payment_timing")}</span>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="default_radio radio_list">
                    <div>
                      <input
                        type="radio"
                        name="pay_period"
                        id="visit"
                        value="visit"
                        checked={paymentType === 'visit'} // 선택 상태 설정
                        onChange={handlePaymentTypeChange} // 핸들러 추가
                      />
                      <label htmlFor="visit">{t("pay_in_store")}</label>
                    </div>
                    <div>
                      <input
                        type="radio"
                        name="pay_period"
                        id="online"
                        value="online"
                        checked={paymentType === 'online'} // 기본값 설정
                        onChange={handlePaymentTypeChange} // 핸들러 추가
                      />
                      <label htmlFor="online">{t("pay_online")}</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>



        {/* 요청사항 */}
        <section className="mt_48 container request_input">
          <div className="order_title">
            <h3>{t("request")}</h3>
            <p className="text_small_b">{t("need_help_0617")}
              <span className="strong_b3 black">02-{storePhone?.csMainPhone?.value}</span>
              {t("need_help_0617_2")}
            </p>
          </div>
          <div className="default_input">
            <textarea
              className="form_input"
              value={memo}
              onChange={handleMemoChange}
              placeholder={t("request_placeholder")}
            />
          </div>
        </section>
      </div>


      {/* 결제정보표시 */}
      <div className="total_payment_con">
        <div className="order_info_box">
          <div className="order_left">
            <div className="top_con flex-8 flex-start">
              <i className="p_icon">P</i>
              <p className="point_info">
                <span>
                  {t('normal_rental_and_return_txt')} {orderSummary?.pointSetting?.payStar1}%(₩{orderSummary?.pointSetting?.payStarMin ? formatLocalString(orderSummary?.pointSetting?.payStarMin) : '0'}) ~ {orderSummary?.pointSetting?.payStar5}%(₩{orderSummary?.pointSetting?.payStarMax ? formatLocalString(orderSummary?.pointSetting?.payStarMax) : '0'}) {t('point_txt')}
                </span>
                <button
                  className="text_small g-80"
                  type="button"
                  onClick={handleEquipmentPopup}
                >
                  {t("equipment_rating_info_0617")}
                </button>
              </p>
            </div>
            <div className="bottom_con">
              <div className="join_con">
                <div className="default_chk_box all_chk">
                  <input
                    type="checkbox"
                    id="allChk"
                    checked={isAllChecked}
                    onChange={handleAllCheck}
                  />
                  <label htmlFor="allChk">
                    <span>{t("order_info_check_0617")}</span>
                  </label>
                </div>

                <ul className="flex-2 column g-90">
                  <li className="default_chk_box">
                    <input
                      type="checkbox"
                      id="useAgree1"
                      name="option1"
                      checked={isChecked.option1}
                      onChange={() => handleCheck('option1')}
                    />
                    <label htmlFor="useAgree1">
                      <span className="flex-btw">
                      {t("order_product_agree_0617")}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('order_product_agreement', e)}>{t("view")}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input
                      type="checkbox"
                      id="useAgree2"
                      name="option2"
                      checked={isChecked.option2}
                      onChange={() => handleCheck('option2')}
                    />
                    <label htmlFor="useAgree2">
                      <span className="flex-btw">
                      {t("order_personal_info_agree_0617")}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('privacy_use_agreement', e)}>{t("view")}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input
                      type="checkbox"
                      id="personalAgree3"
                      name="option3"
                      checked={isChecked.option3}
                      onChange={() => handleCheck('option3')}
                    />
                    <label htmlFor="personalAgree3">
                      <span className="flex-btw">
                      {t("order_personal_info_agree_0617_2")}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('privacy_3rd_agreement', e)}>{t("view")}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input
                      type="checkbox"
                      id="personalAgree4"
                      name="option4"
                      checked={isChecked.option4}
                      onChange={() => handleCheck('option4')}
                    />
                    <label htmlFor="personalAgree4">
                      <span className="flex-btw">
                      {t("order_payment_service_agree_0617")}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('pg_service_agreement', e)}>{t("view")}</div>
                      </span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="order_right">
            <div className="total_detail">
              <div className="total_count">
                <ul className="flex-2 column">
                  <li className="flex-btw">
                      {t("deposit_amount")}
                    <p> ₩ <span>{formatLocalString(orderSummary?.totalInfo?.deposit_price || 0)}</span></p>
                  </li>
                  <li className="flex-btw">
                      {t("insurance_amount")}
                    <p> ₩ <span>{formatLocalString(orderSummary?.totalInfo?.insurance_price || 0)}</span></p>
                  </li>
                  <li className="flex-btw">
                      {t("total_product_amount")}
                    <p> ₩ <span>{formatLocalString(orderSummary?.totalInfo?.sumRentalPrice || 0)}</span></p>
                  </li>
                  <li className="flex-btw">
                      {t("total_discount_amount")}
                    <div style={{display:'flex', alignItems:'center', gap:'20px'}}>
                      {orderSummary?.totalInfo?.discount_rate > 0 && (
                        <p style={{color:'green'}}>{orderSummary?.totalInfo?.discount_rate}%</p>
                      )}
                      <p> ₩ <span>{formatLocalString(orderSummary?.totalInfo?.sumSalePrice || 0)}</span></p>
                    </div>
                  </li>
                  <li className="flex-btw">
                  {t("rental_return_branch")}
                    <p>{selectedRentalStore}<span className="text_small">/</span>{selectedReturnStore}</p>
                  </li>
                  {/* <li className="flex-btw">
                  {t("total_product_quantity3_0617")}
                    <p>{orderSummary?.totalInfo?.total_ea || 0}<span className="text_small">{t("find_email_0617")}</span></p>
                  </li>
                  <li className="flex-btw g-70">
                    &#8735;{t("rental_product4")}
                    <p>{orderSummary?.totalInfo?.total_rental_ea || 0}<span className="cap2_1">{t("find_email_0617")}</span></p>
                  </li>
                  <li className="flex-btw g-70">
                    &#8735;무상품목
                    <p>{orderSummary?.totalInfo?.total_etc_ea || 0}<span className="cap2_1">{t("find_email_0617")}</span></p>
                  </li> */}
                </ul>
              </div>
              <div className="total_day">
                <div className="flex-btw" style={{fontSize:'15px'}}>
                {t("total_rental_days_txt")}
                  <p className="text_small" style={{fontSize:'15px'}}>
                    ({rentalDateTime.date.format('YYYY.MM.DD')}~{returnDateTime.date.format('YYYY.MM.DD')})
                    <span className="strong_b1">{orderSummary?.totalInfo?.rental_period}</span>{t("total_rental_days_count_0417")}
                  </p>
                </div>
              </div>
            </div>
            <div className="total_amount">
              <div className="flex-btw price_box">
                <p className="label red">{t("total_payment_amount_0617")}</p>
                <p className="o_total_price red">
                  ₩<span>{formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)}</span>
                </p>
              </div>
              <p
                className="text_small_b g-70"
                style={{textAlign:'right'}}
              >
                ₩{formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)}{t("total_payment_amount_0617_2")}
              </p>
            </div>

            <div className="payment_btn">
              <button
                type="button"
                className="estimate_btn"
                onClick={() => {
                  // 전달할 데이터 객체 생성
                  const quotationData = {};

                  // 대여 지점이 선택되었다면 추가
                  if (rentalState.selectValue) {
                    quotationData.rentalStore = {
                      selectItem: rentalState.selectItem,
                      selectValue: rentalState.selectValue,
                      storeInfo: rentalState.storeInfo
                    };
                  }

                  // 대여 날짜/시간이 선택되었다면 추가
                  if (rentalDateTime.time) {
                    quotationData.rentalDateTime = {
                      date: rentalDateTime.date.format('YYYY-MM-DD'), // 문자열로 변환
                      time: rentalDateTime.time
                    };
                  }

                  // 반납 지점이 선택되었다면 추가
                  if (returnState.selectValue) {
                    quotationData.returnStore = {
                      selectItem: returnState.selectItem,
                      selectValue: returnState.selectValue,
                      storeInfo: returnState.storeInfo
                    };
                  }

                  // 반납 날짜/시간이 선택되었다면 추가
                  if (returnDateTime.time) {
                    quotationData.returnDateTime = {
                      date: returnDateTime.date.format('YYYY-MM-DD'), // 문자열로 변환
                      time: returnDateTime.time
                    };
                  }

                  // 선택된 상품 리스트 정보도 전달
                  if (selectedProductIds.length > 0) {
                    quotationData.selectedProducts = {
                      ids: selectedProductIds,
                      // 필요한 상품 정보만 선택적으로 전달
                      cartListData: {
                        bundleMain: cartListData.bundleMain?.filter(item =>
                          selectedProductIds.includes(item.cart_option_seq)
                        ),
                        bundleCp: cartListData.bundleCp?.filter(item =>
                          selectedProductIds.includes(item.cart_option_seq)
                        ),
                        bundleAcc: cartListData.bundleAcc?.filter(item =>
                          selectedProductIds.includes(item.cart_option_seq)
                        )
                      }
                    };
                  }

                  // 선택된 데이터가 있으면 state와 함께 이동
                  navigate('/order/quotationForm', {
                    state: Object.keys(quotationData).length > 0 ? quotationData : undefined
                  });
                }}
              >
                {t("inquire_estimate")}
              </button>
              <button
                type="button"
                className="pay_yellow_btn"
                onClick={handlePayment}
                // onClick={handleStockPopup}
              >
                {t("apply_for_rental")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PC 결제창 모달 */}
      {isPayment && (
        <div id="payment" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 102
        }}>
          <div className="dimmed" style={{
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            top: 0,
            left: 0,
            background: 'rgba(0, 0, 0, 0.50)',
            zIndex: 101
          }} />
          <iframe
            id="payment-container"
            name="paymentIframe"
            title={t("payment_window_0617")}
            style={{
              width: '820px',
              height: '600px',
              position: 'relative',
              zIndex: 1000,
              border: 'none'
            }}
          />
        </div>
      )}

      <ProductStockPop
        isPopup={isStockPopup}
        setIsPopup={setStockPopup}
        stockData={stockData}
        onConfirm={handleStockPopupConfirm} // 새로운 핸들러 사용
        onCancel={() => setStockPopup(false)}
      />

      <EquipmentRatingPopup
        isPopup={isEquipmentPopup}
        setIsPopup={setEquipmentPopup}
      />

      <PopupLoginError
        isPopup={isPopupLoginError}
        setIsPopup={setIsPopupLoginError}
        onConfirm={handleConfirm}
        />


      {/* 약관 팝업 */}
      <TermsPopup 
        isPopup={isTermsPopup}
        setIsPopup={setIsTermsPopup}
        title={popupContent.title}
        content={popupContent.content}
      />

    </div>
  )
};
export default VisitOrder;
